import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * KPICard Component
 * Displays a key performance indicator with value, trend, and benchmark comparison
 */
export default function KPICard({
  titulo,
  valor,
  valorAnterior,
  tendencia,
  benchmark,
  descripcion,
  formato = 'numero',
  icono: Icon,
  colorIcono = 'blue'
}) {
  // Format value based on type
  const formatValue = (val) => {
    if (formato === 'porcentaje') {
      return `${val}%`;
    }
    if (formato === 'moneda' || formato === 'moneda_crc') {
      return new Intl.NumberFormat('es-CR', {
        style: 'currency',
        currency: 'CRC',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(val);
    }
    if (formato === 'moneda_usd') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(val);
    }
    if (formato === 'dias') {
      return `${val} dias`;
    }
    return new Intl.NumberFormat('es-CR').format(val);
  };

  // Get trend icon and color
  const getTrendDisplay = () => {
    if (tendencia === 'up') {
      return {
        icon: TrendingUp,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200'
      };
    }
    if (tendencia === 'down') {
      return {
        icon: TrendingDown,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200'
      };
    }
    return {
      icon: Minus,
      color: 'text-gray-500',
      bg: 'bg-gray-50',
      border: 'border-gray-200'
    };
  };

  const trend = getTrendDisplay();
  const TrendIcon = trend.icon;

  // Calculate change percentage
  const changePercent = valorAnterior && valorAnterior !== 0
    ? Math.round(((valor - valorAnterior) / Math.abs(valorAnterior)) * 100)
    : 0;

  // Compare to benchmark
  const benchmarkStatus = benchmark
    ? valor <= benchmark ? 'good' : 'warning'
    : null;

  // Icon color classes
  const iconColors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
    amber: 'bg-amber-50 border-amber-200 text-amber-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600',
    red: 'bg-red-50 border-red-200 text-red-600'
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between mb-3">
        {Icon && (
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center border ${iconColors[colorIcono]}`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
        {tendencia && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${trend.bg} ${trend.color} ${trend.border} border`}>
            <TrendIcon className="h-3 w-3" />
            {changePercent !== 0 && <span>{Math.abs(changePercent)}%</span>}
          </div>
        )}
      </div>

      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {titulo}
        </p>
        <p className="text-2xl font-bold text-gray-900">
          {formatValue(valor)}
        </p>
        {descripcion && (
          <p className="text-xs text-gray-500">{descripcion}</p>
        )}
      </div>

      {benchmark !== undefined && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Benchmark</span>
            <span className={`font-medium ${
              benchmarkStatus === 'good' ? 'text-emerald-600' : 'text-amber-600'
            }`}>
              {formatValue(benchmark)}
            </span>
          </div>
          <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                benchmarkStatus === 'good' ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
              style={{
                width: `${Math.min(100, (valor / benchmark) * 100)}%`
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
